import React, { memo, useMemo, useState, useCallback } from "react";
import { Platform } from "react-native";
import styled from "styled-components/native";
import { Menu, MenuItem } from "@gluestack-ui/themed";
import VDBIcon from "@components/icons/VDBIcon";
import { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { VDBText } from "@components/atoms";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { SearchResultsAndFavoritesEnum } from "@utils/product/searchResultConstants";
import { getProductThemeFromType } from "@components/common/Header/utils";
import { ProductCategory } from "@utils/constants";

interface MenuSectionProps {
  filteredMenuOptions: any[];
  theme: any;
  typography: any;
  productType: string;
  priceMode: PriceModes;
  onMenuItemSelect: (optionName: string) => void;
  itemIndex?: number;
  isInCart?: boolean;
}

const MenuSection = memo<MenuSectionProps>(
  ({
    filteredMenuOptions,
    theme,
    typography,
    productType,
    priceMode,
    onMenuItemSelect,
    itemIndex,
    isInCart = true,
  }) => {
    const [isOpen, setIsOpen] = useState(false);

    const toTestId = (value: string) =>
      value.replace(/\s+/g, "-").toLowerCase();

    const menuItems = useMemo(() => {
      return filteredMenuOptions.map((option, index) => ({
        ...option,
        index,
        isFirst: index === 0,
        isLast: index === filteredMenuOptions.length - 1,
      }));
    }, [filteredMenuOptions]);

    const handleMenuItemPress = useCallback(
      (optionName: string) => {
        setIsOpen(false);
        onMenuItemSelect(optionName);
      },
      [onMenuItemSelect]
    );

    const handleTriggerPress = useCallback(() => {
      setIsOpen((prev) => !prev);
    }, []);

    const productTheme = getProductThemeFromType(
      productType as ProductCategory
    );
    const bgColor =
      isOpen || isInCart
        ? theme[`${productTheme}-06`]
        : productType === ProductCategory.DEFAULT
        ? theme["core-06"]
        : theme[`${productTheme}-03`];

    const iconColor =
      isOpen || isInCart
        ? theme[`${productTheme}-05`]
        : theme[`${productTheme}-06`];

    const threeDotTestId = `grid.result.three-dot-${
      itemIndex != null && itemIndex >= 0 ? itemIndex + 1 : 0
    }`;

    if (!isOpen) {
      return (
        <ThreeDotsContainer
          testID={threeDotTestId}
          backgroundColor={bgColor}
          priceMode={priceMode}
          onPress={handleTriggerPress}
        >
          <VDBIcon
            color={iconColor}
            size={20}
            variant={IconsVariant.VDBCustom}
            name={VDBIcons.DOTS_MENU}
          />
        </ThreeDotsContainer>
      );
    }

    return (
      <Menu
        crossOffset={Platform.OS === "ios" ? 0 : -20}
        offset={0}
        py={0}
        useRNModal={false}
        marginRight={10}
        style={{
          borderRadius: 8,
          boxShadow: "2px 4px 8px rgba(0, 0, 0, 0.4)",
        }}
        shouldFlip={true}
        isOpen={true}
        onClose={() => setIsOpen(false)}
        selectionMode="single"
        trigger={({ ...triggerProps }) => (
          <ThreeDotsContainer
            testID={threeDotTestId}
            backgroundColor={bgColor}
            priceMode={priceMode}
            onPress={handleTriggerPress}
            {...triggerProps}
          >
            <VDBIcon
              color={iconColor}
              size={20}
              variant={IconsVariant.VDBCustom}
              name={VDBIcons.DOTS_MENU}
            />
          </ThreeDotsContainer>
        )}
      >
        {menuItems.map((option) => {
          const optionTestId = `grid.menu.option.${toTestId(
            String(option.name)
          )}`;
          return (
            <MenuItem
              key={option.name}
              textValue={option.label}
              testID={optionTestId}
              data-testId={optionTestId}
              style={{
                padding: 0,
                width: 200,
                backgroundColor: theme["core-01"],
                borderTopLeftRadius: option.isFirst ? 8 : 0,
                borderTopRightRadius: option.isFirst ? 8 : 0,
                borderBottomLeftRadius: option.isLast ? 8 : 0,
                borderBottomRightRadius: option.isLast ? 8 : 0,
              }}
              onPress={() => handleMenuItemPress(option.name)}
            >
              <MenuItemContainer testID={`${optionTestId}.container`}>
                <MenuItemStyle>
                  <VDBText
                    color={
                      option.name ===
                      SearchResultsAndFavoritesEnum.REMOVE_FROM_COLLECTION
                        ? theme["error-01"]
                        : theme["core-05"]
                    }
                    type={typography["Minor-M"]}
                  >
                    {option.label}
                  </VDBText>
                </MenuItemStyle>
                {!option.isLast && <LineView theme={theme} />}
              </MenuItemContainer>
            </MenuItem>
          );
        })}
      </Menu>
    );
  },
  (prev, next) => {
    return (
      prev.filteredMenuOptions === next.filteredMenuOptions &&
      prev.priceMode === next.priceMode &&
      prev.productType === next.productType
    );
  }
);

MenuSection.displayName = "MenuSection";

const ThreeDotsContainer = styled.TouchableOpacity<{
  backgroundColor: string;
  priceMode: string;
}>`
  height: 32px;
  width: 32px;
  border-top-left-radius: 16px;
  border-bottom-right-radius: 8px;
  background-color: ${({ backgroundColor }) => backgroundColor};
  position: absolute;
  bottom: -0.5px;
  right: -0.5px;
  margin-top: ${({ priceMode }) =>
    priceMode === PriceModes.GREEN ? -56 : -36}px;
  align-self: flex-end;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const LineView = styled.View<any>`
  height: 1px;
  background-color: ${({ theme }) => theme["core-03"]};
  width: auto;
`;

const MenuItemStyle = styled.View`
  border-color: ${({ theme }) => theme["core-03"]};
  width: 200px;
  padding: 10px;
`;

const MenuItemContainer = styled.View`
  width: 200px;
`;

export default MenuSection;
